/**
 * RiderDeductionFilterSection Component
 * 라이더 근무 현황 검색 필터 영역 (컨테이너)
 */
import React, { useMemo } from 'react';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { RiderWorkStatusFilters, Supplier } from '../types/riderWorkStatus.types';
import DateFilterSection from "@/features/settlement/riderWorkStatus/components/filter/DateFilterSection";
import WorkDaysFilterInput from "@/features/settlement/riderWorkStatus/components/filter/WorkDaysFilterInput";
import '@/shared/styles/search-form.css';

interface FilterSectionProps {
    filters: RiderWorkStatusFilters;
    suppliers: Supplier[];
    suppliersLoading: boolean;
    isSupplierUser: boolean;
    loading: boolean;
    excelDownloading: boolean;
    onFilterChange: (key: keyof RiderWorkStatusFilters, value: string) => void;
    onSearch: () => void;
    onExcelDownload: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         filters,
                                                         suppliers,
                                                         suppliersLoading,
                                                         isSupplierUser,
                                                         loading,
                                                         excelDownloading,
                                                         onFilterChange,
                                                         onSearch,
                                                         onExcelDownload,
                                                     }) => {
    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier) => ({
            value: supplier.centerId,
            label: supplier.name,
        }));
    }, [suppliers]);

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className={`search-form work-status-search-form ${isSupplierUser ? 'supplier-view' : ''}`}>
                    {/* 조회 기간 + 프리셋 버튼 */}
                    <DateFilterSection
                        fromDate={filters.fromDate}
                        toDate={filters.toDate}
                        onFilterChange={onFilterChange}
                    />

                    {/* 협력사 (본사 사용자만) */}
                    {!isSupplierUser && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filters.suppId || ''}
                                    onChange={(value) => onFilterChange('suppId', value)}
                                    placeholder={suppliersLoading ? '로딩 중...' : '협력사 검색...'}
                                    disabled={suppliersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 최소 근무일수 필터 */}
                    <WorkDaysFilterInput
                        minWorkDays={filters.minWorkDays}
                        onFilterChange={onFilterChange}
                        onSearch={onSearch}
                    />

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button
                            className="btn btn-success excel-btn"
                            onClick={onExcelDownload}
                            disabled={excelDownloading || !filters.fromDate || !filters.toDate}
                        >
                            {excelDownloading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    다운로드 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-excel"></i>
                                    엑셀
                                </>
                            )}
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i>
                                    조회 중...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search"></i>
                                    검색
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
